import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiMessageSquare } from 'react-icons/fi';

import type { CollaborationTarget } from './collaboration-types';
import { useCollaborationOptional } from './CollaborationContext';

type CollaborationDiscussButtonProps = {
  target: CollaborationTarget;
  variant?: 'icon' | 'compact' | 'card';
  className?: string;
};

export default function CollaborationDiscussButton({
  target,
  variant = 'compact',
  className = '',
}: CollaborationDiscussButtonProps) {
  const { t } = useTranslation('ClinicalCollaboration');
  const collaboration = useCollaborationOptional();

  if (!collaboration) {
    return null;
  }

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    collaboration.openCollaboration(target);
  };

  if (variant === 'icon') {
    return (
      <button
        type="button"
        onClick={handleClick}
        title={t('discussHere')}
        className={`rounded-lg p-2 text-slate-500 transition-colors hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-950/40 ${className}`}
      >
        <FiMessageSquare size={16} />
      </button>
    );
  }

  if (variant === 'card') {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={`inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 shadow-sm transition-colors hover:border-blue-300 hover:text-blue-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 ${className}`}
      >
        <FiMessageSquare size={12} />
        {t('discuss')}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-blue-950/30 ${className}`}
    >
      <FiMessageSquare size={14} />
      {t('discuss')}
    </button>
  );
}
